import { PackageTemplate, RRDate } from '@raskrask-dk/raskrask-utils'
import { Button, PartnerCard, Text } from '@raskrask-dk/rr-components'
import { t } from 'i18next'
import { Fragment, ReactElement } from 'react'
import { GoalBF, PartnerBF, ServiceBF } from '../../models'
import PartnerCardText from './partnercardtext'

export const RenderGoal = (
  selectedGoal: GoalBF,
  onClick: (goalBf: GoalBF) => void
): ((selectedGoal: GoalBF) => ReactElement) => {
  return (goal) =>
    Mapper.mapToGoal(
      goal,
      goal.id === selectedGoal?.id ?? 0,
      goal.id,
      onClick
    )
}

export const RenderServiceType = (
  selectedService: ServiceBF | undefined,
  onClick: (service: ServiceBF) => void
): ((service: ServiceBF) => ReactElement) => {
  return (service) =>
    Mapper.mapToService(
      service,
      service.id === selectedService?.id ?? 0,
      service.id,
      onClick
    )
}

export const RenderPartnerCard = (
  selectedPartner: PartnerBF | undefined,
  onClick: (selectedPartner: PartnerBF) => void,
  selectedService: ServiceBF,
  isMobile: boolean,
  isCompanyBooking: boolean,
  selectedDate?: RRDate
): ((selectedPartner: PartnerBF) => ReactElement) => {
  return (partner) =>
    Mapper.mapToPartner(
      partner,
      isMobile,
      onClick,
      selectedService,
      selectedPartner,
      isCompanyBooking,
      selectedDate
    )
}

export const RenderPartnerKlippeCard = (
  selectedPartner: PartnerBF | undefined,
  template: PackageTemplate | undefined,
  onClick: (selectedPartner: PartnerBF) => void,
  selectedService: ServiceBF,
  isMobile: boolean
): ((selectedPartner: PartnerBF) => ReactElement) => {
  return (partner) =>
    Mapper.mapToPartnerKlippekort(
      partner,
      template,
      isMobile,
      onClick,
      selectedService,
      selectedPartner
    )
}

class Mapper {
  public static mapToGoal = (
    goal: GoalBF,
    isActive = false,
    key = 1,
    onClick: (goalBF: GoalBF) => void
  ): ReactElement => {
    return (
      <Fragment key={goal.id}>
        <Button
          active={isActive}
          data-cy={`calendarGoal_${key + 1}`}
          disabled={goal.isDisabled}
          onClick={() => onClick(goal)}
          size="large"
          variant="option"
        >
          <Text>{t(`common:goals.${goal.id}`)}</Text>
        </Button>
      </Fragment>
    )
  }

  public static mapToPartner = (
    partner: PartnerBF,
    isMobile: boolean,
    onClick: (partner: PartnerBF) => void,
    selectedService: ServiceBF,
    selectedPartner: PartnerBF | undefined,
    isCompanyBooking: boolean,
    selectedDate?: RRDate
  ): ReactElement => {
    const partnerPrice = partner.getPrice(selectedService)
    const priceForCard =
      partnerPrice === 0 ? t('calendar.prices.free') : `${partnerPrice},-`
    const firstDay = partner.getFirstAvOfPartner(selectedService)
    const timeSlots =
      selectedDate == undefined
        ? []
        : partner.schedule
          .timeslotsByDay(selectedDate, selectedService.duration)
          .map((p) => p.from.toString('HH:mm'))
    // const firstDay = partner.schedule.firstDayAvailable(
    //     selectedService.duration
    // )
    return (
      <PartnerCard
        alias={partner.alias}
        backgroundWhite={selectedDate !== undefined}
        isSelected={selectedPartner === partner}
        image={partner.therapistPictureLink}
        key={partner.id}
        noReviewsText={t('common:newatraskrask')}
        numberOfReviews={partner.numberOfReviews}
        onClick={() => onClick(partner)}
        price={priceForCard}
        rating={partner.rating}
        // TODO: Text this line is dirty and shoud be refactored
        text={
          <PartnerCardText
            t={t}
            firstAVTime={
              firstDay === 'NO_AVAILABLE' ? undefined : firstDay
            }
            timeSlots={timeSlots}
          />
        }
        vatText={
          isCompanyBooking ? t(`common:priceMomsText`) : undefined
        }
        data-cy={`partnercard`}
      />
    )
  }

  public static mapToPartnerKlippekort = (
    partner: PartnerBF,
    template: PackageTemplate | undefined,
    isMobile: boolean,
    onClick: (partner: PartnerBF) => void,
    selectedService: ServiceBF,
    selectedPartner: PartnerBF | undefined
  ): ReactElement => {
    const priceForCard =
      template instanceof PackageTemplate
        ? `${template.getSaveFromAndPriceFrom(partner).priceFrom},-`
        : ''

    const firstDay = partner.getFirstAvOfPartner(selectedService)

    // const firstDay = partner.schedule.firstDayAvailable(
    //     selectedService.duration
    // )
    return (
      <PartnerCard
        alias={partner.alias}
        backgroundWhite={false}
        isSelected={selectedPartner === partner}
        image={partner.therapistPictureLink}
        key={partner.id}
        noReviewsText={t('common:newatraskrask')}
        numberOfReviews={partner.numberOfReviews}
        onClick={() => onClick(partner)}
        price={priceForCard}
        rating={partner.rating}
        text={
          <PartnerCardText
            t={t}
            firstAVTime={
              firstDay === 'NO_AVAILABLE' ? undefined : firstDay
            }
            timeSlots={[]}
          />
        }
        vatText={
          template instanceof PackageTemplate
            ? `${t('klippekort.price.saveLabel')} ${template.getSaveFromAndPriceFrom(partner).saveMin
            },-`
            : undefined
        }
        data-cy={`partnercard`}
      ></PartnerCard>
    )
  }

  public static mapToService = (
    service: ServiceBF,
    isActive = false,
    key = 1,
    onClick: (serviceBF: ServiceBF) => void
  ): ReactElement => {
    return (
      <Fragment key={service.id}>
        <Button
          active={isActive}
          data-cy={`calendarService_${key + 1}`}
          key={service.id}
          onClick={() => onClick(service)}
          size="large"
          variant="option"
        >
          <Text>
            {service.freeConsultation
              ? t(`calendar.servicetypes.freeconsultationtext`)
              : `${service.duration.toMillis() / 60 / 1000} min`}
          </Text>
        </Button>
      </Fragment>
    )
  }
}

export { Mapper }
